import type React from 'react';
import { useEffect, useState } from 'react';
import { Button, Input } from '../ui';
import { useToast } from '../Toast';
import { supabase } from '../../lib/supabase';
import { fetchAccent, saveAccent, applyAccent, parseColour } from '../../lib/accent';
import { ACCENT_PRESETS } from '../../lib/accent-presets';

// ===========================================================================
// Appearance panel - runtime brand accent (admin/seasonal lock).
// This intentionally diverges from the brand handoff's "build-time, no JS,
// one commit" delivery (the team asked to set it from /admin without a
// deploy) but keeps brand *intent*: admin-only, exactly one accent at a
// time, pure black-&-white by default. Presets = the vetted v5 palette;
// a custom colour is allowed but flagged. See src/lib/accent.ts.
// ===========================================================================
export function AppearancePanel({ active }: { active: boolean }) {
  const toast = useToast();
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverValue, setServerValue] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [customInput, setCustomInput] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const v = await fetchAccent();
      setServerValue(v.accent);
      setSelected(v.accent);
      setCustomInput(v.accent ?? '');
      setInputError(null);
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }

  useEffect(() => {
    if (active && !loaded) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  function pickPreset(hex: string) {
    setSelected(hex);
    setCustomInput(hex);
    setInputError(null);
  }

  function onCustomChange(raw: string) {
    setCustomInput(raw);
    if (raw.trim() === '') {
      setSelected(null);
      setInputError(null);
      return;
    }
    try {
      setSelected(parseColour(raw));
      setInputError(null);
    } catch (e: any) {
      setInputError(e?.message ?? 'Invalid colour.');
    }
  }

  function resetBW() {
    setSelected(null);
    setCustomInput('');
    setInputError(null);
  }

  const dirty = (selected ?? null) !== (serverValue ?? null);
  const isVetted = selected
    ? ACCENT_PRESETS.some((p) => p.hex.toLowerCase() === selected.toLowerCase())
    : true; // B&W is the brand default

  async function save() {
    if (inputError) {
      toast.error('Fix the colour first.');
      return;
    }
    setSaving(true);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) throw new Error('Sign in first.');
      const stored = await saveAccent({ accent: selected, accent_strong: selected }, token);
      applyAccent(stored); // repaint the whole app for this admin immediately
      setServerValue(stored.accent);
      setSelected(stored.accent);
      setCustomInput(stored.accent ?? '');
      toast.success(
        stored.accent
          ? 'Accent saved. Other members pick it up on next load (within ~1 min).'
          : 'Reset to black & white. Other members update on next load.',
      );
    } catch (err: any) {
      toast.error(err?.message ?? 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  if (!loaded && loading) {
    return (
      <>
        <div className="admin__panel-head"><h2>Appearance</h2></div>
        <p className="admin__note">Loading…</p>
      </>
    );
  }

  // Scoped preview: redeclare --accent/--accent-strong on the wrapper so
  // the sample reflects `selected` without touching the rest of the app.
  const previewVars = {
    ['--accent']: selected ?? '#14110F',
    ['--accent-strong']: selected ?? '#FAFAF7',
  } as React.CSSProperties;

  return (
    <>
      <div className="admin__panel-head">
        <h2>Appearance</h2>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={refresh}
          disabled={loading || saving}
        >
          {loading ? '…' : 'Refresh'}
        </Button>
      </div>
      <p className="admin__note">
        The brand is black-&-white by default. Set <strong>one</strong> accent and it
        cascades across the app: rules, buttons, pills, links, tinted halftones.
        Headlines and body stay carbon. This is an admin/seasonal decision; members
        never pick.
      </p>

      <div className="admin__panel-head" style={{ marginTop: 'var(--space-6)' }}>
        <h3 style={{ margin: 0 }}>Brand palette</h3>
      </div>
      <div className="appearance__swatches">
        {ACCENT_PRESETS.map((p) => {
          const on = selected?.toLowerCase() === p.hex.toLowerCase();
          return (
            <button
              key={p.name}
              type="button"
              className={`appearance__swatch ${on ? 'appearance__swatch--on' : ''}`}
              style={{ background: p.hex }}
              onClick={() => pickPreset(p.hex)}
              title={`${p.family} · ${p.tone} (${p.hex})`}
              aria-label={`${p.family} ${p.tone}`}
              aria-pressed={on}
            >
              {on ? <span className="appearance__swatch-check" aria-hidden>✓</span> : null}
            </button>
          );
        })}
      </div>

      <div className="admin__panel-head" style={{ marginTop: 'var(--space-6)' }}>
        <h3 style={{ margin: 0 }}>Custom colour</h3>
      </div>
      <div className="admin__add">
        <div className="admin__add-row">
          <label style={{ flex: 1 }}>
            <span>Hex or RGB</span>
            <Input
              type="text"
              placeholder="#C97D58 or rgb(201, 125, 88)"
              value={customInput}
              onChange={(e) => onCustomChange(e.target.value)}
              disabled={saving}
              spellCheck={false}
            />
            {inputError && (
              <small style={{ display: 'block', marginTop: 4, color: 'var(--text-soft)' }}>
                {inputError}
              </small>
            )}
          </label>
          <label>
            <span>Pick</span>
            <input
              type="color"
              value={selected ?? '#141110'}
              onChange={(e) => {
                setSelected(e.target.value);
                setCustomInput(e.target.value);
                setInputError(null);
              }}
              disabled={saving}
              style={{ width: 56, height: 40, padding: 2 }}
              aria-label="Colour picker"
            />
          </label>
        </div>
        {!isVetted && (
          <small style={{ display: 'block', marginTop: 6, color: 'var(--text-soft)' }}>
            Heads up, this is outside the brand-vetted v5 palette. The brand
            team's #1 rule is restraint; prefer a preset unless there's a
            deliberate reason.
          </small>
        )}
      </div>

      <div className="admin__panel-head" style={{ marginTop: 'var(--space-6)' }}>
        <h3 style={{ margin: 0 }}>Preview</h3>
      </div>
      <div className="appearance__preview" style={previewVars}>
        <span className="appearance__preview-rule" aria-hidden />
        <Button type="button" variant="accent" size="sm" tabIndex={-1}>
          Accent button
        </Button>
        <span className="pill pill--badge" style={{ ['--chip']: 'var(--accent)' } as React.CSSProperties}>
          Pill
        </span>
        <a
          href="#appearance"
          onClick={(e) => e.preventDefault()}
          style={{ color: 'var(--accent)', fontWeight: 600 }}
        >
          A link
        </a>
        <span className="appearance__preview-label">
          {selected ? selected : 'Black & white (default)'}
        </span>
      </div>

      <div className="admin__row-actions" style={{ marginTop: 'var(--space-6)', gap: 'var(--space-4)' }}>
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={save}
          disabled={saving || !dirty || !!inputError}
        >
          {saving ? '…' : dirty ? 'Save accent' : 'Saved'}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={resetBW}
          disabled={saving || selected === null}
        >
          Reset to black &amp; white
        </Button>
        {dirty && (
          <span style={{ color: 'var(--text-soft)', fontSize: '0.9em', alignSelf: 'center' }}>
            Unsaved
          </span>
        )}
      </div>
    </>
  );
}
